import { ampli } from '@/ampli';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getABTests, getUUID } from '@/app/common/amplitude/events/ampliEventsUtils.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { HotelStaticData } from '@/app/components/hotel-detail/common/staticHotelDataAdapter.ts';
import type { InitialParams } from '@/app/components/hotel-detail/InitialParamsProvider.tsx';
import type { OffersData } from '@/app/components/hotel-detail/OffersProvider.tsx';
import {
  getAccommodationType,
  getResponseCodeWithoutRooms,
  getRoomOptionQty,
  PRODUCT_DETAILS_RESPONSE_CODE_ROOMS,
} from '@/app/common/tracking/trackingUtils.ts';
import { consentCategoryMeasurementGiven } from '@/app/common/utils/consentCategoryMeasurementGiven.ts';
import { canUseAmplitude } from '@/app/common/amplitude/amplitudeUtils.ts';
import type { Localization } from '@/app/components/search/common/Localization';

type AmpliProductDetailsShownWithRoomsType = {
  searchInfoData: SearchInfoData;
  hotelStaticData: HotelStaticData | null | undefined;
  initialParams: InitialParams;
  offersData: OffersData;
  commonData: CommonData;
  localization: Localization;
  packageMealPlan: MealPlanType | undefined; // preselected package in Evo, cheapest in legacy
};
export const ampliProductDetailsShownWithRooms = ({
  searchInfoData,
  hotelStaticData,
  initialParams,
  offersData,
  commonData,
  localization,
  packageMealPlan,
}: AmpliProductDetailsShownWithRoomsType) => {
  if (!canUseAmplitude(commonData, localization)) return;

  const { searchId, searchType } = searchInfoData;
  const { abTests } = commonData;

  const accommodationType = getAccommodationType(hotelStaticData);
  const amenitiesFlag = (hotelStaticData?.amenities.length ?? 0) > 0;
  const descriptionFlag = (hotelStaticData?.description?.length ?? 0) > 0;
  const detailPageId = initialParams.productDetailKey || '';
  const internalHotelId = `${initialParams.internalHotelId || 0}`;
  const mealPlanClass = packageMealPlan?.boardClass || '';
  const providerId = packageMealPlan?.supplier?.id?.toString() || '';
  const roomOptionQty = getRoomOptionQty(offersData);
  const roomQty = offersData?.rooms?.length ?? 0;
  const totalPriceEur = packageMealPlan?.finalTotalPriceEUR ?? 0;
  const uuid = getUUID(commonData);

  ampli.productDetailsShown({
    ...getABTests(abTests),
    'accommodation type': accommodationType,
    'amenities flag': amenitiesFlag,
    'description flag': descriptionFlag,
    'detail page id': detailPageId,
    'features enabled': [], // TODO: Implement in a future iteration
    'internal hotel id': internalHotelId,
    'meal plan class': mealPlanClass,
    'provider id': providerId,
    'response code': PRODUCT_DETAILS_RESPONSE_CODE_ROOMS,
    'room option qty': roomOptionQty,
    'room qty': roomQty,
    'search id': searchId,
    'search type': searchType,
    'total price eur': totalPriceEur,
    uuid,
  });
};

type AmpliProductDetailsShownWithoutRoomsType = {
  searchInfoData: SearchInfoData;
  hotelStaticData: HotelStaticData | null | undefined;
  initialParams: InitialParams;
  offersData: OffersData | undefined;
  commonData: CommonData;
  isPastCheckin: boolean;
};
export const ampliProductDetailsShownWithoutRooms = ({
  searchInfoData,
  hotelStaticData,
  initialParams,
  offersData,
  commonData,
  isPastCheckin,
}: AmpliProductDetailsShownWithoutRoomsType) => {
  if (!consentCategoryMeasurementGiven(commonData)) return;

  const { searchId, searchType } = searchInfoData;
  const { abTests } = commonData;

  const amenitiesFlag = (hotelStaticData?.amenities.length ?? 0) > 0;
  const descriptionFlag = (hotelStaticData?.description?.length ?? 0) > 0;
  const detailPageId = initialParams.productDetailKey || '';
  const internalHotelId = `${initialParams.internalHotelId || 0}`;
  const responseCode = getResponseCodeWithoutRooms(offersData, isPastCheckin);
  const uuid = getUUID(commonData);

  ampli.productDetailsShown({
    ...getABTests(abTests),
    'accommodation type': '',
    'amenities flag': amenitiesFlag,
    'description flag': descriptionFlag,
    'detail page id': detailPageId,
    'features enabled': [], // TODO: Implement in a future iteration
    'internal hotel id': internalHotelId,
    'meal plan class': '',
    'provider id': '',
    'response code': responseCode,
    'room option qty': 0,
    'room qty': 0,
    'search id': searchId,
    'search type': searchType,
    'total price eur': 0,
    uuid,
  });
};
